/**
 * Core State Types - Discriminated unions for type-safe state management
 * @module @one-payments/core/types
 */

/**
 * Payment Method IDs - const assertion for string literal types
 */
export const PAYMENT_METHODS = {
  CARD: 'card',
  PAYNOW: 'paynow',
  PROMPTPAY: 'promptpay',
  DUITNOW: 'duitnow',
  GOPAY: 'gopay',
  BOOST: 'boost',
  SHOPEEPAY: 'shopeepay',
  ATOM: 'atom',
  DANA: 'dana',
  TNG: 'tng',
  ALIPAYCN: 'alipaycn',
  ALIPAYHK: 'alipayhk',
  GCASH: 'gcash',
  KONBINI: 'konbini',
  PAYEASY: 'payeasy',
  GRABPAY_SG: 'grabpay_sg',
  FPX: 'fpx',
} as const;

export type PaymentMethodId = (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];

/**
 * Payment Status - Discriminated union
 */
export const PAYMENT_STATUS = {
  IDLE: 'idle',
  INITIALIZING: 'initializing',
  READY: 'ready',
  PROCESSING: 'processing',
  REQUIRES_ACTION: 'requires_action',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

/**
 * Currency codes (ISO 4217)
 *
 * While we define common currencies as constants for convenience,
 * the CurrencyCode type accepts any valid ISO 4217 three-letter code
 * since the API can work with any currency.
 */
export const CURRENCIES = {
  USD: 'USD',
  EUR: 'EUR',
  GBP: 'GBP',
  SGD: 'SGD',
  THB: 'THB',
  MYR: 'MYR',
  IDR: 'IDR',
  JPY: 'JPY',
  AUD: 'AUD',
  CAD: 'CAD',
  CHF: 'CHF',
} as const;

/**
 * Currency code type - accepts any ISO 4217 three-letter currency code
 *
 * While common currencies are defined in CURRENCIES constant,
 * this type accepts any string to support all possible currencies
 */
export type CurrencyCode = string;

/**
 * Environment
 *
 * Public API only exposes 'prod' and 'demo'.
 * 'dev' is intentionally excluded from the type to prevent external usage,
 * but remains functional at runtime for internal development.
 */
export const ENVIRONMENTS = {
  PRODUCTION: 'prod',
  DEMO: 'demo',
  /** @internal - Not exposed in Environment type. For internal use only. */
  DEVELOPMENT: 'dev',
} as const;

/**
 * Environment type for SDK configuration.
 *
 * Only 'prod' and 'demo' are publicly supported.
 * Using 'dev' will cause a TypeScript error but will work at runtime.
 */
export type Environment = 'prod' | 'demo';

/**
 * Payment Intent - strict typing
 */
export interface PaymentIntent {
  readonly id: string;
  readonly amount: number;
  readonly currency: CurrencyCode;
  readonly status: PaymentStatus;
  readonly metadata?: Record<string, string | number | boolean>;
}

/**
 * Payment Method Config
 */
export interface PaymentMethodConfig {
  readonly id: PaymentMethodId;
  readonly enabled: boolean;
  readonly priority: number;
}

/**
 * SDK Configuration - all fields strictly typed
 */
export interface SDKConfig {
  readonly apiKey: string;
  readonly secretKey: string;
  readonly environment: Environment;
  readonly apiBaseUrl?: string;
  readonly locale?: string;
  readonly paymentMethods?: readonly PaymentMethodConfig[];
}

/**
 * Payment Error - strongly typed error object
 */
export interface PaymentError {
  readonly code: string;
  readonly message: string;
  readonly details?: Record<string, unknown>;
  readonly timestamp: number;
}

/**
 * State - Discriminated Union Pattern for Type Safety
 */
export type State =
  | { readonly status: 'idle' }
  | { readonly status: 'initializing' }
  | {
      readonly status: 'ready';
      readonly paymentIntent: PaymentIntent;
      readonly availableMethods: readonly PaymentMethodConfig[];
    }
  | {
      readonly status: 'processing';
      readonly paymentIntent: PaymentIntent;
      readonly selectedMethod: PaymentMethodId;
      readonly availableMethods: readonly PaymentMethodConfig[];
    }
  | {
      readonly status: 'requires_action';
      readonly paymentIntent: PaymentIntent;
      readonly actionUrl: string;
      readonly selectedMethod: PaymentMethodId;
      readonly availableMethods: readonly PaymentMethodConfig[];
    }
  | {
      readonly status: 'succeeded';
      readonly paymentIntent: PaymentIntent;
      readonly selectedMethod: PaymentMethodId;
    }
  | {
      readonly status: 'failed';
      readonly error: PaymentError;
      readonly paymentIntent?: PaymentIntent;
    };

/**
 * Type guard functions for state discrimination
 */
export function isIdleState(state: State): state is Extract<State, { status: 'idle' }> {
  return state.status === 'idle';
}

export function isInitializingState(
  state: State,
): state is Extract<State, { status: 'initializing' }> {
  return state.status === 'initializing';
}

export function isReadyState(state: State): state is Extract<State, { status: 'ready' }> {
  return state.status === 'ready';
}

export function isProcessingState(state: State): state is Extract<State, { status: 'processing' }> {
  return state.status === 'processing';
}

export function isRequiresActionState(
  state: State,
): state is Extract<State, { status: 'requires_action' }> {
  return state.status === 'requires_action';
}

export function isSucceededState(state: State): state is Extract<State, { status: 'succeeded' }> {
  return state.status === 'succeeded';
}

export function isFailedState(state: State): state is Extract<State, { status: 'failed' }> {
  return state.status === 'failed';
}
